import { Fragment, useEffect, useRef, useState } from 'react';

import { ExpandableText } from '@components/expandable-text';
import { createForm, createFormStore } from '@components/form';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { InfoBox } from '@components/info-box';
import { useIntegrationEndpoint } from '@components/integrations';
import { Spinner } from '@components/loading-spinner';
import { SelectOption } from '@components/select';
import { forceHideSidebar, Sidebar } from '@components/sidebar';
import { SquareImage } from '@components/square-image';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  FieldMappingGroup,
  FieldType,
  VariableValue,
} from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';

import { useMarketplaceTemplate, useTemplateEndpoint } from 'api/app-marketplace/hooks';
import { createJourneyActionDocumentPath } from 'api/documents/requests';
import { useUploadFile } from 'api/files/hooks';
import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import { TimeSavedData } from 'api/journeys/requests';
import { SavedCustomField } from 'api/types';
import { ACTION_TYPES } from 'ui/actions/action-config';
import { TimeSavedContainer } from 'ui/helpers';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import {
  ActionFooter,
  ActionHeader,
  ActionSidebarError,
  ActionSidebarLoading,
  DescriptionFormField,
  SidebarProps,
  useCreateActionFormStore,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';

import { AppNotConnectedSection, DisabledSection, SidebarWarningSection } from './common';
import {
  CustomFieldsEditor,
  CustomFieldsEditorHandle,
  useCustomFieldParentContext,
} from './custom-fields';
import { extractRequestData } from './extract-request-data';
import { IntegrationField, sidebarLocale } from './integration-field';

import { TriggerMethod } from '../common';
import { getFieldMappingId } from '../helpers';
import { extractRequestHeaders } from '../shared/field-utils';

type IntegrationData = {
  id: string;
  description: string;
  isAutomatic: boolean;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
  integrationEndpointId: string;
  continueOnError: boolean;
  customFields?: SavedCustomField[];
} & Record<string, { value: string; variables: object } | string> &
  TimeSavedData;

const ACTION_TYPE = ACTION_TYPES.integration;
const Form = createForm<IntegrationData>();

export const SidebarContent = (props: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<typeof ACTION_TYPE>(journeyActionId);
  const [isTemplate, setIsTemplate] = useState(false);
  const templateEndpointId = data?.values?.templateEndpointId;
  const integrationEndpointId = data?.values?.integrationEndpointId?.value;
  useEffect(() => {
    if (templateEndpointId && !integrationEndpointId) {
      setIsTemplate(true);
    } else if (integrationEndpointId) {
      setIsTemplate(false);
    }
  }, [templateEndpointId, integrationEndpointId]);

  if (isLoading || !data) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }
  if (!templateEndpointId && !integrationEndpointId) {
    return <ActionSidebarError actionType={ACTION_TYPE} text={t('integrationError')} {...props} />;
  }
  return isTemplate && templateEndpointId ? (
    <TemplateSidebar templateEndpointId={templateEndpointId} {...props} />
  ) : (
    <EndpointSidebar {...props} />
  );
};

const EndpointSidebar = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<typeof ACTION_TYPE>(journeyActionId);
  const values = data?.values;
  const { t } = useScopedTranslate(sidebarLocale);
  const {
    data: endpointData,
    isLoading: endpointIsLoading,
    isError: endpointIsError,
  } = useIntegrationEndpoint(
    values?.integrationEndpointId?.value ?? '',
    // Very hacky way to make sure that when you edit the variable default value in the integration builder and create a new action that it doesn't use the old default value.
    {
      queryKeyHashFn: (queryKey: string[]) => {
        return queryKey + journeyActionId;
      },
      cacheTime: 0,
    },
  );
  const templateId = endpointData?.templateId ?? undefined;
  const { data: template, isInitialLoading: templateLoading } = useMarketplaceTemplate(templateId);
  const hasAuthError = Boolean(template && template.connectionStatus !== 'connected');
  const variables = endpointData?.variables ?? [];
  const requestHeaders = (endpointData?.requestHeaders ?? []).filter((h) => h.visible);
  const hasCustomFields = Boolean(endpointData?.customFieldConfigShortId);
  const showRequestSection = variables.length > 0 || hasCustomFields;
  const formStore = useCreateActionFormStore();
  const isDirty = formStore.useIsDirty();
  const editMutation = useUpdateJourneyAction(journeyActionId, { onSuccess: () => false });
  const uploadMutation = useUploadFile(
    createJourneyActionDocumentPath(data?.journey ?? '', journeyActionId),
    true,
  );
  const fieldMappingId = getFieldMappingId(data?.journey ?? '');

  const parentContext = useCustomFieldParentContext(endpointData, formStore, values?.requestData);

  const customFieldsEditorRef = useRef<CustomFieldsEditorHandle>(null);
  const [isCustomFieldsDirty, setIsCustomFieldsDirty] = useState(false);

  const handleSave = async (data: IntegrationData, shouldSkipClosing?: boolean) => {
    await editMutation.mutateAsync({
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      action: ACTION_TYPE,
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        integrationEndpointId: values?.integrationEndpointId,
        requestData: extractRequestData(data, variables),
        requestHeaders: extractRequestHeaders(data),
        continueOnError: data.continueOnError,
        customFields: customFieldsEditorRef.current?.getCustomFields() ?? [],
      },
      timeSaved: {
        days: data.days,
        hours: data.hours,
        minutes: data.minutes,
      },
    });
    setIsCustomFieldsDirty(false);
    !shouldSkipClosing && forceHideSidebar();
  };

  const variableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
  });

  const emailVariableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    types: ['email'],
  });

  const dateVariableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    types: ['date'],
  });

  const optionsVariableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    types: ['select'],
  });

  const fileVariableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    types: ['file'],
  });

  const getVariableGroups = (fieldType: FieldType) => {
    const typeGroupMap: Partial<Record<FieldType, FieldMappingGroup[]>> = {
      email: emailVariableGroups,
      date: dateVariableGroups,
      select: optionsVariableGroups,
      file: fileVariableGroups,
    };

    return typeGroupMap[fieldType] ?? variableGroups;
  };

  // Hack start - Even worse hacky method to save the integration  if it hasn't been saved before and has default values
  const noValuesSaved =
    !endpointIsLoading &&
    (!data?.values || Object.keys(data.values.requestData ?? {}).length === 0);
  const hasDefaultValues =
    variables.filter((variable) => Boolean(variable.default_value)).length > 0 ||
    (endpointData?.defaultFiles ?? []).length > 0;

  const needsToSaveDefaultValues = noValuesSaved && hasDefaultValues;

  useEffect(() => {
    if (needsToSaveDefaultValues) {
      const formData = formStore.getState().values as IntegrationData;
      handleSave(formData, true);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [needsToSaveDefaultValues]);
  // Hack end

  if (isLoading || !data || endpointIsLoading || templateLoading) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }

  const Container = hasAuthError ? DisabledSection : Fragment;

  return (
    <Sidebar>
      <ActionHeader
        actionType={ACTION_TYPE}
        title={endpointData?.name}
        customIcon={
          endpointData?.thumbnail ? <SquareImage src={endpointData.thumbnail} /> : undefined
        }
        subtitle={endpointData?.integrationName}
        subtitleFont="regular12"
        subtitleLocation="below"
        {...props}
      />
      <Sidebar.Content>
        <FormWithUnsavedChangesPrompt store={formStore} onSubmit={(data) => handleSave(data)}>
          {endpointIsError ? (
            <InfoBox
              variant="red"
              role="alert"
              icon={<IconFormIndicatorError />}
              text={t('integrationError')}
            />
          ) : (
            <Stack orientation="vertical" space={6}>
              {hasAuthError && (
                <SidebarWarningSection noMargin>
                  <AppNotConnectedSection
                    template={template}
                    journeyActionId={props.journeyActionId}
                  />
                </SidebarWarningSection>
              )}
              <Container>
                <HideContainer title={t('actionDetails')}>
                  <Stack space={4}>
                    <ExpandableText text={endpointData?.description} />
                    <DescriptionFormField defaultValue={data.description} />
                  </Stack>
                </HideContainer>
                <Sidebar.Separator />
                <HideContainer title={t('triggerMethod')}>
                  <ExecutionMethod
                    assignees={data.assignees}
                    isAutomatic={data.isAutomatic}
                    formStore={formStore}
                    hideLabel
                  />
                </HideContainer>
                {showRequestSection && (
                  <>
                    <Sidebar.Separator />
                    <HideContainer title={t('requestValues')}>
                      <Stack space={4}>
                        {endpointIsLoading && <Spinner />}
                        {variables.map((field) => (
                          <IntegrationField
                            key={field.name}
                            field={field}
                            allFields={variables}
                            defaultValue={values?.requestData?.[field.name]}
                            savedRequestData={values?.requestData}
                            variableGroups={getVariableGroups(field.type as FieldType)}
                            uploadMutation={uploadMutation}
                            defaultFiles={endpointData?.defaultFiles}
                            isNewAction={noValuesSaved}
                            fieldMappingId={fieldMappingId}
                            formStore={formStore}
                          />
                        ))}
                        <CustomFieldsEditor
                          ref={customFieldsEditorRef}
                          endpointData={endpointData}
                          savedCustomFields={values?.customFields ?? []}
                          formStore={formStore}
                          variableGroupsByType={getVariableGroups}
                          fieldMappingId={fieldMappingId}
                          parentContext={parentContext}
                          onDirtyChange={() => setIsCustomFieldsDirty(true)}
                        />
                      </Stack>
                    </HideContainer>
                  </>
                )}
                {requestHeaders.length > 0 && (
                  <>
                    <Sidebar.Separator />
                    <Text font="bold14" text={t('requestHeaders')} />
                    {requestHeaders.map((header) => (
                      <Form.FieldMappingTextEditor
                        key={header.name}
                        id={`requestHeaders__${header.name}`}
                        label={header.label}
                        defaultValue={
                          values?.requestHeaders?.[header.name] ??
                          (header.default_value
                            ? ({ value: header.default_value } as VariableValue)
                            : undefined)
                        }
                        variableGroups={variableGroups}
                        singleLine
                        plaintext
                        fieldMappingId={fieldMappingId}
                      />
                    ))}
                  </>
                )}
              </Container>
            </Stack>
          )}
          <Sidebar.Separator />
          <Container>
            <HideContainer title={t('advanced')} defaultClosed>
              <Form.Checkbox
                // @ts-ignore
                id="continueOnError"
                label={t('continueOnError')}
                defaultValue={values?.continueOnError ?? false}
              />
            </HideContainer>
            <Sidebar.Separator />
            <TimeSavedContainer {...data.timeSaved} />
          </Container>
          <ActionFooter
            action={data}
            isDisabled={
              (!isDirty && !isCustomFieldsDirty) || editMutation.isLoading || hasAuthError
            }
            form={Form}
          />
        </FormWithUnsavedChangesPrompt>
      </Sidebar.Content>
    </Sidebar>
  );
};

const TemplateSidebar = ({
  templateEndpointId,
  ...props
}: SidebarProps & { templateEndpointId: string }) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const {
    data: endpointData,
    isLoading: endpointLoading,
    isError: endpointIsError,
  } = useTemplateEndpoint(templateEndpointId);
  const { data: template, isInitialLoading: templateLoading } = useMarketplaceTemplate(
    endpointData?.templateId,
  );
  const variables = endpointData?.variables ?? [];

  if (endpointIsError) {
    return <ActionSidebarError actionType={ACTION_TYPE} text={t('integrationError')} {...props} />;
  }

  if (endpointLoading || !endpointData || templateLoading) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }

  return (
    <Sidebar>
      <ActionHeader
        actionType={ACTION_TYPE}
        title={endpointData?.name}
        subtitle={endpointData?.integrationName}
        subtitleFont="regular12"
        subtitleLocation="below"
        customIcon={
          endpointData?.thumbnail ? <SquareImage src={endpointData.thumbnail} /> : undefined
        }
        {...props}
      />
      <Sidebar.Content>
        <TemplatePreviewForm onSubmit={() => {}}>
          <Stack space={6}>
            <AppNotConnectedSection template={template} journeyActionId={props.journeyActionId} />
            <Sidebar.Separator />
            <DisabledSection>
              <HideContainer title={t('actionDetails')}>
                <Stack space={4}>
                  <ExpandableText text={endpointData?.description} />
                  <DescriptionFormField defaultValue="" />
                </Stack>
              </HideContainer>
              <Sidebar.Separator />
              <HideContainer title={t('triggerMethod')}>
                <ExecutionMethod
                  assignees={[]}
                  isAutomatic
                  formStore={createFormStore()}
                  hideLabel
                />
              </HideContainer>
              {variables.length > 0 && (
                <>
                  <Sidebar.Separator />
                  <HideContainer title={t('requestValues')}>
                    <Stack space={4}>
                      {variables.map((field) => (
                        <IntegrationField
                          key={field.name}
                          field={field}
                          allFields={variables}
                          defaultValue={undefined}
                          variableGroups={[]}
                          uploadMutation={null as unknown as ReturnType<typeof useUploadFile>}
                          defaultFiles={undefined}
                          isNewAction={false}
                          fieldMappingId=""
                        />
                      ))}
                    </Stack>
                  </HideContainer>
                </>
              )}
            </DisabledSection>
          </Stack>
          <ActionFooter
            action={{
              shortId: props.journeyActionId,
              action: ACTION_TYPE,
              description: undefined,
            }}
            isDisabled={true}
            form={TemplatePreviewForm}
          />
        </TemplatePreviewForm>
      </Sidebar.Content>
    </Sidebar>
  );
};

const TemplatePreviewForm = createForm<Record<string, unknown>>();
